import React, { useState, useRef } from 'lib/react';
import {
  EVApps,
  EVButton, EVKeyboardArrowUp, EVKeyboardArrowDown, EVKeyboardArrowRight,
  EVPopper, EVClickAwayListener, EVPaper, EVList, EVListItem, EVRefreshOutlinedIcon,
  EVGrid, EVDrawer, EVArrowBackIcon, EVSvgIcon,
} from 'lib/components';
import clsx from 'lib/clsx';
import { useTranslation } from 'lib/translation';
import useMediaQuery from 'hooks/useMediaQuery';
import { useSelector, useDispatch } from 'lib/redux';
import Typography from 'commonComponents/typography';
import CustomSvgIcon from 'commonComponents/customSvgIcon';
import SkeletonLoader from 'commonComponents/skeletonLoader';
import { GET_USER_ENTITLED_APPS } from 'constants/ActionTypes';
import { HIDDEN_APPS } from 'constants/AppConstants';
import AppsMenuStyles from './AppsMenuStyles';

function AppsMenu() {
  const classes = AppsMenuStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);
  const isTablet = useMediaQuery(1024);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const appsMenuRef = useRef();

  const currentURL = window.location.pathname;
  const { entitledApps, entitledAppsLoading, entitledAppsError } = useSelector(({ userData }) => ({
    entitledApps: userData?.entitledApps,
    entitledAppsLoading: userData.loading.entitledApps,
    entitledAppsError: userData.error.entitledApps,
  }));

  const handleAppsMenuButtonClick = () => {
    setAnchorEl(appsMenuRef.current);
    setIsMenuOpen((isOpen) => !isOpen);
  };

  const handleMenuClickAway = () => {
    setIsMenuOpen(false);
  };
  const isAppSelected = (appCode) => currentURL.split('/')?.[1] === `${appCode}`;

  const appsMenuLoader = () => (
    <EVGrid className={classes.loaderContainer}>
      <SkeletonLoader />
      <SkeletonLoader />
      <SkeletonLoader />
    </EVGrid>
  );

  const appsMenuList = () => {
    if (entitledAppsLoading) return appsMenuLoader();
    if (entitledAppsError) {
      return (
        <EVButton
          id="retryButton"
          variant="outlined"
          color="primary"
          className={classes.retryBtn}
          classes={{ label: classes.retryLabel }}
          onClick={() => dispatch({ type: GET_USER_ENTITLED_APPS })}
        >
          <EVRefreshOutlinedIcon />
          {t('order_history__retry')}
        </EVButton>
      );
    }

    const filteredApps = entitledApps?.filter((app) => !HIDDEN_APPS.includes(app.appCode));

    return filteredApps?.map((menuOption) => {
      const svgComponent = () => <CustomSvgIcon svgString={menuOption.icon} color={isAppSelected(menuOption.appCode) ? 'white' : '#61687D'} />;
      return (
        <EVListItem key={menuOption.appCode} className={classes.listStyle}>
          <EVButton
            id={`${menuOption.appCode}-app-btn`}
            className={clsx(classes.menuOptionItem, isAppSelected(menuOption.appCode) && 'selected')}
            href={`/${menuOption.appCode}`}
            onClick={handleMenuClickAway}
            aria-label={t(`${menuOption.name}`)}
          >
            <EVSvgIcon component={svgComponent} />
            {t(`${menuOption.name}`)}
          </EVButton>
        </EVListItem>
      );
    });
  };

  if (isMobile || isTablet) {
    return (
      <>
        <EVButton
          id="apps-menu-mobile-button"
          className={clsx(classes.appsMenuMobileBtn, isMenuOpen && 'open')}
          onClick={() => handleAppsMenuButtonClick()}
          ref={appsMenuRef}
          aria-expanded
          aria-controls="apps-menu-mobile"
        >
          <EVGrid className={classes.appsIconWithText}>
            <EVApps
              className={clsx(classes.appsIcon, isMenuOpen && classes.appsIconOpen)}
            />
            <Typography>{t('common_apps')}</Typography>
          </EVGrid>
          <EVKeyboardArrowRight className={classes.keyboardArrowRight} />
        </EVButton>

        <EVDrawer
          id="apps-menu-drawer"
          className={classes.appMenuDrawer}
          anchor="left"
          open={isMenuOpen}
          hideBackdrop
          PaperProps={{
            className: classes.appsMenuPaper,
          }}
        >
          <EVButton
            id="apps-drawer-back-button"
            className={classes.appsDrawerBackBtn}
            onClick={() => handleMenuClickAway()}
          >
            <EVArrowBackIcon />
            {t('common_back')}
          </EVButton>
          <EVList disablePadding className={classes.appsList}>
            {appsMenuList()}
          </EVList>
        </EVDrawer>
      </>
    );
  }
  return (
    <>
      <EVButton
        id="apps-menu-button"
        className={clsx(classes.appsMenuBtn, isMenuOpen && 'open')}
        onClick={() => handleAppsMenuButtonClick()}
        ref={appsMenuRef}
        aria-expanded
        aria-controls="apps-menu-btn"
      >
        <EVApps
          className={clsx(classes.appsIcon, isMenuOpen && classes.appsIconOpen)}
        />

        { isMenuOpen
          ? <EVKeyboardArrowUp className={clsx(classes.keyboardArrow, 'up')} />
          : <EVKeyboardArrowDown className={clsx(classes.keyboardArrow, 'down')} />}
      </EVButton>

      <EVPopper
        id="apps-menu-popper"
        className={classes.popper}
        open={isMenuOpen}
        placement="bottom-end"
        anchorEl={anchorEl}
        disablePortal
      >
        <EVClickAwayListener onClickAway={handleMenuClickAway}>
          <EVPaper
            className={classes.appsMenuPaper}
            elevation={6}
            component="nav"
          >
            <Typography className={classes.popperHeading}>{t('common_apps')}</Typography>
            <EVList disablePadding className={classes.appsList}>
              {appsMenuList()}
            </EVList>
          </EVPaper>
        </EVClickAwayListener>
      </EVPopper>
    </>
  );
}

export default AppsMenu;
